<template>
  <modal
    ref="passwordChangeModal"
    template="neutral"
    ref-name="passwordChangeModal"
    :icon-enabled="false"
    :close-button-enabled="true"
    class="password-change-modal"
  >
    <button
      class="close-button btn-transparent"
      @click="closePasswordChangeModal"
    >
      <md-icon>clear</md-icon>
    </button>
    <div
      class="password-change-modal__wrapper"
      :class="{ 'password-change-modal__wrapper--loading': loading }"
    >
      <spinner
        v-if="loading"
        class="loading"
      />
      <template v-else>
        <h3 class="title">
          Alterar Senha
          <br>
          <strong
            v-if="temporaryPassword && !localPasswordChanged"
            class="temporary-message"
          >
            Você está usando uma senha temporária. Por favor, altere-a!
          </strong>
        </h3>
        <p class="subtitle">
          Informe sua senha atual e defina a nova senha:
        </p>
        <form class="new-form-component">
          <!-- Campo para senha atual -->
          <div class="input-wrapper">
            <label
              v-if="passwordData.oldPassword"
              for="oldPassword"
            >Senha Atual</label>
            <md-icon class="input-icon left">
              lock_outline
            </md-icon>
            <input
              id="oldPassword"
              v-model="passwordData.oldPassword"
              :type="showOldPassword ? 'text' : 'password'"
              placeholder="Senha Atual"
              required
            >
            <a @click.prevent="togglePassword('old')">
              <md-icon class="input-icon right">
                {{ showOldPassword ? "visibility_off" : "visibility" }}
              </md-icon>
            </a>
          </div>
          <!-- Campo para nova senha -->
          <div
            class="input-wrapper progress-bar"
            :class="'progress-bar--' + strengthPercentage"
          >
            <label
              v-if="passwordData.newPassword"
              for="newPassword"
            >Nova Senha</label>
            <md-icon class="input-icon left">
              lock_outline
            </md-icon>
            <input
              id="newPassword"
              v-model="passwordData.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              placeholder="Nova Senha"
              required
            >
            <a @click.prevent="togglePassword('new')">
              <md-icon class="input-icon right">
                {{ showNewPassword ? "visibility_off" : "visibility" }}
              </md-icon>
            </a>
          </div>
          <!-- Campo para confirmar nova senha -->
          <div class="input-wrapper">
            <label
              v-if="passwordData.passwordConfirm"
              for="passwordConfirm"
            >Confirmar Nova Senha</label>
            <md-icon class="input-icon left">
              lock_outline
            </md-icon>
            <input
              id="passwordConfirm"
              v-model="passwordData.passwordConfirm"
              :type="showConfirmPassword ? 'text' : 'password'"
              placeholder="Confirme a Nova Senha"
              required
            >
            <a @click.prevent="togglePassword('confirm')">
              <md-icon class="input-icon right">
                {{ showConfirmPassword ? "visibility_off" : "visibility" }}
              </md-icon>
            </a>
          </div>
          <!-- Mensagem de erro para confirmação -->
          <span
            v-if="
              passwordData.passwordConfirm &&
                passwordData.newPassword !== passwordData.passwordConfirm
            "
            class="err"
          >
            *As senhas não coincidem
          </span>
          <!-- Regras de senha -->
          <div class="password-rules">
            <h6>Força da senha:</h6>
            <ul>
              <li
                v-for="(rule, index) in passwordRules"
                :key="index"
              >
                <svg-loader
                  v-if="rule.validator(passwordData.newPassword)"
                  src="/static/images/perfil-de-acesso/checked-pass.svg"
                />
                <svg-loader
                  v-else
                  src="/static/images/perfil-de-acesso/wrong-pass.svg"
                />
                <span v-html="rule.description" />
              </li>
            </ul>
          </div>
          <button
            :disabled="!canChangePassword"
            class="md-button btn btn-primary btn-new"
            @click.prevent="changePassword"
          >
            Alterar Senha
          </button>
        </form>
      </template>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import userService from "@/api/authentication";
import notify from "@/api/notification";
import { getCookie, saveCookie } from '@/utils/auth-utils'

import Spinner from "@/components/common/Spinner";

export default {
  name: "PasswordChangeModal",
  components: {
    Spinner,
  },
  data() {
    return {
      loading: false,
      localPasswordChanged: getCookie('passwordChanged'),
      passwordData: {
        oldPassword: "",
        newPassword: "",
        passwordConfirm: "",
      },
      passwordRules: [
        {
          description: "Deve ter no mínimo <strong>8 caracteres</strong>",
          validator: (password) => password.length >= 8,
        },
        {
          description: "Deve ter pelo menos uma <strong>letra maiúscula</strong>",
          validator: (password) => /[A-Z]/.test(password),
        },
        {
          description: "Deve ter pelo menos uma <strong>letra minúscula</strong>",
          validator: (password) => /[a-z]/.test(password),
        },
        {
          description: "Deve ter pelo menos um <strong>número</strong>",
          validator: (password) => /\d/.test(password),
        },
      ],
      showNewPassword: false,
      showOldPassword: false,
      showConfirmPassword: false,
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    strengthPercentage() {
      const rulesDone = this.passwordRules.filter((rule) =>
        rule.validator(this.passwordData.newPassword)
      ).length;
      return (rulesDone / this.passwordRules.length) * 100;
    },
    canChangePassword() {
      return (
        this.passwordData.oldPassword !== "" &&
        this.strengthPercentage === 100 &&
        this.passwordData.newPassword !== "" &&
        this.passwordData.newPassword === this.passwordData.passwordConfirm
      );
    },
    temporaryPassword() {
      return this.profile && this.profile.temporaryPassword && !this.localPasswordChanged;
    },
  },
  methods: {
    openPasswordChangeModal() {
      this.$refs.passwordChangeModal.open();
    },
    closePasswordChangeModal() {
      this.$refs.passwordChangeModal.close();
      this.passwordData.oldPassword = "";
      this.passwordData.newPassword = "";
      this.passwordData.passwordConfirm = "";
    },
    togglePassword(type) {
      if (type === "old") {
        this.showOldPassword = !this.showOldPassword;
      }
      if (type === "new") {
        this.showNewPassword = !this.showNewPassword;
      }
      if (type === "confirm") {
        this.showConfirmPassword = !this.showConfirmPassword;
      }
    },
    setPasswordChanged(value) {
      this.localPasswordChanged = value;
      saveCookie('passwordChanged', value);
    },
    changePassword() {
      this.loading = true;

      userService
        .changePassword(this.passwordData)
        .then(() => {
          this.loading = false;
          this.setPasswordChanged(true);
          this.closePasswordChangeModal();

          notify({
            title: "SUCESSO:",
            message: "Senha alterada com sucesso!",
          });
        })
        .catch((err) => {
          this.loading = false;
          console.error("Erro ao realizar a alteração da senha:", err);

          if (err.response?.data?.code === "CURRENT_PASSWORD_INVALID") {
            notify({
              title: 'ERRO:',
              message: 'Senha Atual Invalida!'
            })
          } else {
            notify({
              title: "ERRO:",
              message: "Erro ao realizar a alteração da senha!",
            });
          }
        });
    },
  },
};
</script>

<style lang="scss">
.password-change-modal {
  .md-dialog {
    width: 90%;
    min-width: 400px;
    max-width: 400px;
    padding: 0;

    &-content {
      padding: 0;
    }

    .md-dialog-title {
      display: none;
    }
  }

  &__wrapper {
    padding: 40px;

    &--loading {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .title {
      font-weight: 700;
      font-size: 18px;
      color: #595959;
    }

    .temporary-message {
      font-size: 14px;
      color: #d9534f;
      margin: 8px 0;
    }

    .subtitle {
      font-size: 14px;
      color: #363636;
      margin-top: 16px;
    }

    .new-form-component {
      display: flex;
      flex-direction: column;

      .input-wrapper {
        margin-top: 16px;
        position: relative;
        padding: 0 0 0 8px;

        &.progress-bar {
          background-color: #ffffff !important;
          box-shadow: none;
        }

        input {
          padding-left: 36px;
        }

        label {
          font-size: 12px;
          margin-bottom: 4px;
          display: block;
          color: #363636;
        }

        .input-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
        }

        a {
          width: 45px;
          position: relative;
          cursor: pointer;

          .input-icon {
            left: 0;
          }
        }
      }

      .password-rules {
        margin-top: 12px;

        h6 {
          font-weight: 700;
          font-size: 13.33px;
          color: #666666;
        }

        ul {
          margin-top: 4px;

          li {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            font-weight: 400;
            font-size: 12px;
            color: #363636;
            margin-top: 4px;

            .md-icon {
              margin: 0;
              margin-right: 8px;
            }
          }
        }
      }

      .err {
        font-size: 11px;
        color: red;
      }

      .btn-new {
        margin: 24px auto 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}
</style>
